class LocalizationFormComponent extends HTMLElement {
  constructor() {
    super();
    this.form = null;
    this.languageInput = null;
    this.countryInput = null;
    this.countryInputItems = null;
  }

  connectedCallback() {
    // On utilise l'ID exceptionnellement à la place des data-ref car c'est une balise {% form %}
    this.form = this.querySelector('#LocalizationForm');
    this.languageInput = this.querySelector('[data-ref="language-input"]');
    this.countryInput = this.querySelector('[data-ref="country-input"]');
    this.countryInputItems = this.querySelectorAll('[data-ref="country-input-item"]');

    // NBO: sur téléphone, un glissement dans la liste pouvait être compris comme un clic
    // sur une ligne et changer le pays sans qu'on le veuille (le pays reste ensuite en
    // mémoire sur tout le site). On ignore donc les clics qui suivent un déplacement du doigt.
    this.touchMoved = false;
    this.addEventListener(
      'touchstart',
      (event) => {
        this.touchMoved = false;
        this.touchStart = event.touches[0];
      },
      { passive: true },
    );
    this.addEventListener(
      'touchmove',
      (event) => {
        if (!this.touchStart) return;
        const touch = event.touches[0];
        const moved =
          Math.abs(touch.clientX - this.touchStart.clientX) > 8 ||
          Math.abs(touch.clientY - this.touchStart.clientY) > 8;
        if (moved) this.touchMoved = true;
      },
      { passive: true },
    );

    // On écoute les changements de pays
    this.countryInputItems.forEach((countryInputItem) => {
      countryInputItem.addEventListener('click', (event) => {
        if (this.touchMoved) {
          event.preventDefault();
          this.touchMoved = false;
          return;
        }
        this.selectCountry(countryInputItem.dataset.value, event);
      });
    });

    // NBO: type to filter the country list; Enter picks the first match
    this.countrySearch = this.querySelector('[data-ref="country-search"]');
    this.countrySearch?.addEventListener('input', () => this.filterCountries());
    this.countrySearch?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      // NBO: on ne valide que s'il ne reste qu'un seul pays. Avec la touche "OK" du clavier
      // mobile, valider le premier résultat changeait le pays par accident.
      const visible = [...this.countryInputItems].filter((item) => !item.hidden);
      if (visible.length === 1) this.selectCountry(visible[0].dataset.value, event);
    });

    // On écoute les changements de langue
    this.languageInput?.addEventListener('change', (event) => {
      this.changeLanguage(event);
    });

    this.watchPanelPosition();
  }

  // NBO: sur téléphone la liste des pays s'ouvrait collée en bas de l'écran, là où le clavier
  // vient se placer dès qu'on tape : la fenêtre passait derrière le clavier. On la replace donc
  // sous le bouton, avec la hauteur réellement visible (visualViewport tient compte du clavier).
  watchPanelPosition() {
    this.dropdown = this.closest('dropdown-component');
    if (!this.dropdown) return;

    this.toggle = this.dropdown.querySelector('[data-ref="dropdown-toggle"]');
    this.panel = this.dropdown.querySelector('[data-ref="dropdown-content"]');
    if (!this.toggle || !this.panel) return;

    this.positionPanel = () => {
      if (this.dropdown.dataset.open !== 'true') return;
      if (window.matchMedia('(width > 1000px)').matches) {
        this.panel.style.removeProperty('top');
        this.panel.style.removeProperty('bottom');
        this.panel.style.removeProperty('max-height');
        return;
      }

      const top = Math.max(8, Math.round(this.toggle.getBoundingClientRect().bottom) + 8);
      const viewport = Math.round(window.visualViewport?.height || window.innerHeight);
      this.panel.style.top = `${top}px`;
      this.panel.style.bottom = 'auto';
      this.panel.style.maxHeight = `${Math.max(180, viewport - top - 8)}px`;
    };

    new MutationObserver(this.positionPanel).observe(this.dropdown, { attributeFilter: ['data-open'] });
    window.visualViewport?.addEventListener('resize', this.positionPanel);
    window.addEventListener('resize', this.positionPanel);
    window.addEventListener('scroll', this.positionPanel, { passive: true });
  }

  disconnectedCallback() {
    this.countryInputItems.forEach((countryInputItem) => {
      countryInputItem.removeEventListener('click', (event) => {
        this.selectCountry(countryInputItem.dataset.value, event);
      });
    });

    this.languageInput?.removeEventListener('change', (event) => {
      this.changeLanguage(event);
    });
  }

  selectCountry = (countryName, event) => {
    event.preventDefault();

    this.countryInput.value = countryName;
    this.form?.submit();
  };

  filterCountries() {
    const normalize = (text) =>
      text
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .trim();
    const query = normalize(this.countrySearch.value);

    this.countryInputItems.forEach((item) => {
      const name = normalize(item.querySelector('.country')?.textContent || '');
      const code = item.dataset.value.toLowerCase();
      item.hidden = query !== '' && !name.includes(query) && code !== query;
    });
  }

  changeLanguage(event) {
    const value = event.target instanceof HTMLSelectElement ? event.target.value : null;

    if (value) {
      this.languageInput.value = value;
      this.form?.submit();
    }
  }
}

if (!customElements.get('localization-form-component')) {
  customElements.define('localization-form-component', LocalizationFormComponent);
}
